/**
 * The browser's view of "who is signed in", and the gate in front of the console.
 *
 * Supabase Auth owns the session (security.md §3): the browser holds only the publishable key and a
 * user's own tokens, and every API call carries the access token as a bearer that the api Worker
 * verifies against the project's JWKS. Nothing here decides what the user may see — that is RLS and
 * the API's job (CLAUDE.md §2 never 3). This module only answers "is there a session" so the shell
 * can render for someone and the login page for no one.
 */
import { ErrorState, LoadingState } from '@flowza/ui'
import type { Session } from '@supabase/supabase-js'
import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { configStatus, getClient } from '../supabase'
import { ProvisionOrganization } from './provision'

export type SessionState =
  /** No Supabase URL or publishable key in this build: nothing can sign in. */
  | { readonly kind: 'unconfigured'; readonly missing: readonly string[] }
  | { readonly kind: 'loading' }
  | { readonly kind: 'signed_out' }
  | {
      readonly kind: 'signed_in'
      readonly userId: string
      readonly email: string | null
      /**
       * True when the access token carries no `org_id`, which means this account has no
       * organization yet (ADR 0023). A UI signal, never an authorization one: the API re-verifies
       * the token and decides for itself, and this only chooses which screen to render.
       */
      readonly needsOrganization: boolean
      /**
       * The `org_id` claim, for reads that must name the organization the user is signed INTO.
       * RLS admits every org a user belongs to; the console shows one. Same trust level as
       * `needsOrganization`: a hint for the screen, re-derived by Postgres from the same token.
       */
      readonly orgId: string | null
    }

/**
 * Read one claim out of the access token.
 *
 * Reading a JWT in the browser is exactly as trustworthy as the browser, which is why nothing
 * SECURITY-bearing is decided from it — the api Worker verifies the signature and reads the same
 * claim itself. What it buys is a screen chosen without a round trip that would 401 anyway: an
 * account with no organization cannot successfully call anything, so asking the API first would
 * only turn a known state into a failed request.
 */
function claimOf(session: Session, claim: string): unknown {
  const token = session.access_token
  if (typeof token !== 'string') return undefined
  const payload = token.split('.')[1]
  if (payload === undefined) return undefined
  try {
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(padded + '='.repeat((4 - (padded.length % 4)) % 4)))[claim]
  } catch {
    // A token this code cannot read is one the API will reject anyway; treating it as "no claim"
    // sends the user through provisioning, which refuses it in turn. Both paths end somewhere
    // honest, and neither is a crash in a render.
    return undefined
  }
}

function stateOf(session: Session | null): SessionState {
  if (session === null) return { kind: 'signed_out' }
  const claim = claimOf(session, 'org_id')
  const orgId = typeof claim === 'string' && claim.length > 0 ? claim : null
  return {
    kind: 'signed_in',
    userId: session.user.id,
    email: session.user.email ?? null,
    needsOrganization: orgId === null,
    orgId,
  }
}

/** The current session, kept current by Supabase's own auth events (sign-in, sign-out, refresh). */
export function useSession(): SessionState {
  const [state, setState] = useState<SessionState>(() => {
    if (getClient() !== null) return { kind: 'loading' }
    const config = configStatus()
    return { kind: 'unconfigured', missing: config.ok ? [] : config.missing }
  })

  // Subscribed once per mount. The client is resolved inside the effect rather than captured as a
  // dependency: its identity is not what this hook is about, and a client that is re-created (a test
  // double, a future re-configuration) must not restart the subscription on every render.
  useEffect(() => {
    const client = getClient()
    if (client === null) return
    let cancelled = false
    void client.auth.getSession().then(({ data }) => {
      if (!cancelled) setState(stateOf(data.session))
    })
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_event, session) => {
      if (!cancelled) setState(stateOf(session))
    })
    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
  }, [])

  return state
}

export async function signOut(): Promise<void> {
  const client = getClient()
  if (client !== null) await client.auth.signOut()
}

/**
 * The gate: signed in renders the children; signed out goes to the login page and remembers where
 * it was heading; an unconfigured build says which variables are missing instead of looping to a
 * login page that could not work either.
 */
export function RequireSession({ children }: { readonly children: ReactNode }) {
  const session = useSession()
  const location = useLocation()
  switch (session.kind) {
    case 'unconfigured':
      return (
        <main className="mx-auto max-w-lg px-4 py-12">
          <ErrorState
            title="This deployment is not configured"
            body={`Missing build variables: ${session.missing.join(', ')}. Set them in the Pages project under Settings → Environment variables, then redeploy.`}
          />
        </main>
      )
    case 'loading':
      return (
        <main className="mx-auto max-w-lg px-4 py-12">
          <LoadingState label="Checking your session" />
        </main>
      )
    case 'signed_out':
      /*
        Two doors, and which one a person is sent to is decided by where they were heading.

        `/adm` is guarded by this same component, so before the staff door existed a staff member
        who opened a colleague's `/adm/tenants` link was shown the CUSTOMER sign-in: a branded panel
        selling the product, an offer to create an account, and no hint that the place they were
        going has a second requirement. `returnTo` already carried them back to `/adm` afterwards
        (ADR 0186), so it worked — it just said the wrong things on the way, and said nothing at all
        about the authenticator the staff gate is about to demand.

        The prefix test is `/adm` exactly or `/adm/…`, never `startsWith('/adm')` on its own: that
        would also catch a future `/admin` and quietly route a tenant screen to the staff door.
      */
      return (
        <Navigate
          to={
            location.pathname === '/adm' || location.pathname.startsWith('/adm/')
              ? '/adm/login'
              : '/auth/login'
          }
          replace
          state={{ from: `${location.pathname}${location.search}` }}
        />
      )
    case 'signed_in':
      // A signed-in account with no organization cannot successfully call anything, so it gets the
      // provisioning step rather than a console that 401s on every request (ADR 0023). Once the
      // refreshed token carries claims this branch renders the children as usual.
      return session.needsOrganization ? (
        <ProvisionOrganization>{children}</ProvisionOrganization>
      ) : (
        <>{children}</>
      )
  }
}
